import React, { useEffect, useState, useMemo } from "react";
// react plugin used to create charts
import { Line, Bar, Doughnut } from "react-chartjs-2";

// reactstrap components
import {
  Input,
  Table,
  Row,
  Col,
  Container,
  Card,
  CardBody,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
} from "reactstrap";

import {
  dashboardPaymentStatusCount,
  dashboardJobStatusCount,
  dashboardQuotationRequestedForApproval,
  dashboardSparePartsReadyForPickup,
  dashboardOdooSyncSummary,
  dashboardWorkOrderLineChart,
  downloadCsv,
  dashboardActionRequired,
  dashboardQuotationStatusCount,
  dashboardSparePartsStatusCount,
  odooSalesCount,
  odooQuotationCount,
} from "actions/dashboard";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import { useDispatch, useSelector } from "react-redux";
import { fixedWidthCustomStyle } from "components/Common/constants";
import { getCustomerList } from "actions/customer";
import moment from "moment";
import DatePicker from "react-datepicker";

// --- Filter options ---
const filterOptions = [
  { value: "7-days", label: "7 Days", days: 7 },
  { value: "15-days", label: "15 Days", days: 15 },
  { value: "30-days", label: "30 Days", days: 30 },
  { value: "60-days", label: "60 Days", days: 60 },
  { value: "90-days", label: "90 Days", days: 90 },
  { value: "6 Months", label: "6 Months", days: 180 },
  { value: "12 Months", label: "12 Months", days: 365 },
  { value: "CUSTOM", label: "Custom", days: null },

];

const odooFilterOptions = [
  { value: "ALL", label: "All", days: null },
  ...filterOptions,
];

const customerSelectStyles = {
  ...fixedWidthCustomStyle(true),
  control: (base, state) => ({
    ...base,
    minHeight: "2.375rem",
    backgroundColor: "#fff",
    borderColor: state.isFocused ? "#3b82f6" : "#e2e8f0",
    borderRadius: "0.6rem",
    boxShadow: state.isFocused
      ? "0 0 0 3px rgba(59, 130, 246, 0.15)"
      : "0 1px 2px rgba(15, 23, 42, 0.04)",
    transition: "border-color 0.18s ease, box-shadow 0.18s ease",
    "&:hover": {
      borderColor: state.isFocused ? "#3b82f6" : "#cbd5e1",
      boxShadow: state.isFocused
        ? "0 0 0 3px rgba(59, 130, 246, 0.15)"
        : "0 2px 6px rgba(15, 23, 42, 0.06)",
    },
  }),
};

const daysSelectStyles = {
  container: (base) => ({
    ...base,
    width: "100%",
  }),
  control: (base) => ({
    ...base,
    minHeight: 0,
    height: "auto",
    border: 0,
    borderRadius: 0,
    backgroundColor: "transparent",
    boxShadow: "none",
    cursor: "pointer",
    "&:hover": { border: 0 },
  }),
  valueContainer: (base) => ({
    ...base,
    minWidth: 0,
    padding: "0.5rem 2.1rem",
    fontSize: "0.78rem",
  }),
  singleValue: (base) => ({
    ...base,
    color: "#1f2937",
    fontSize: "0.78rem",
    fontWeight: 600,
  }),
  input: (base) => ({ ...base, margin: 0, padding: 0 }),
  indicatorsContainer: () => ({ display: "none" }),
  menu: (base) => ({
    ...base,
    zIndex: 2000,
    minWidth: "100%",
    marginTop: "0.4rem",
    borderRadius: "0.4rem",
  }),
  menuList: (base) => ({ ...base, paddingTop: 0, paddingBottom: 0 }),
  menuPortal: (base) => ({ ...base, zIndex: 2000 }),
};

// --- Job Status Mapping (API Status -> Label & Color) ---
const JOB_STATUS_CONFIG = {
  JOB_ALLOCATED: { label: "Work Order Allocated", color: "#2A5B86" },
  QUOTATION_IN_PROGRESS: { label: "Quotation In Progress", color: "#8b5cf6" },
  QUOTATION_REJECTED: { label: "Quotation Rejected", color: "#9ca3af" },
  REQUEST_APPROVAL: { label: "Request Approval", color: "#3b82f6" },
  QUOTATION_ACCEPTED: { label: "Quotation Accepted", color: "#10b981" },
  PART_REQUESTS: { label: "Part Requests", color: "#30AFFF" },
  REPAIR: { label: "Repair", color: "#f59e0b" },
  REPAIRED: { label: "Repaired", color: "#2A7C13" },
  INVOICE_PAID: { label: "Invoice Paid", color: "#165823" },
  INVOICE_NOT_PAID: { label: "Invoice Not Paid", color: "#ef4444" },
  CANCELLED: { label: "Cancelled", color: "#D90000" },
  JOB_REJECTED_BY_CLIENT: { label: "Work Order Rejected By Client", color: "#7f7f7f" },
};

const QUOTATION_STATUS_CONFIG = {
  QUOTATION_CREATED: { label: "Quotation Created", color: "#2A5B86" },
  QUOTATION_REQUESTED_FOR_APPROVAL: {
    label: "Requested For Approval",
    color: "#EDC92C",
  },
  QUOTATION_APPROVED: { label: "Quotation Approved", color: "#66BB6A" },
  QUOTATION_ACCEPTED: { label: "Quotation Accepted", color: "#2A835F" },
  QUOTATION_REVISED: { label: "Quotation Revised", color: "#f59e0b" },
  QUOTATION_REJECTED: { label: "Quotation Rejected", color: "#E63946" },
};

const SPARE_PART_STATUS_CONFIG = {
  PENDING: { label: "Pending", color: "#D97A2B" },
  PART_ON_ORDER: { label: "Part On Order", color: "#f59e0b" },
  READY_FOR_PICKUP: { label: "Ready For Pickup", color: "#3b82f6" },
  PICKED_UP: { label: "Picked Up", color: "#6984A9" },
  COMPLETED: { label: "Completed", color: "#0D530E" },
};

// --- Odoo Integration Mapping ---
const ODOO_MODULE_LABELS = {
  CUSTOMERS: "Customers",
  MANUFACTURERS: "Manufacturers",
  SERVICES: "Services",
  SPARE_PARTS: "Spare Parts",
  QUOTATIONS: "Quotations",
};

const ODOO_MODULE_ORDER = [
  "CUSTOMERS",
  "SPARE_PARTS",
  "SERVICES",
  "MANUFACTURERS",
  "QUOTATIONS",
];

// ✅ Only these 6 reports are exportable.
const EXPORT_REPORTS = [
  {
    key: "workorderline",
    label: "Bar Chart",
    description: "Work order bar chart",
    icon: "fa-solid  fa-bar-chart",
  },
  {
    key: "jobstatus",
    label: "Work Order Status",
    description: "Work order status counts",
    icon: "fa-solid fa-clipboard-list",
  },
  {
    key: "quotation",
    label: "Quotation Status",
    description: "Quotation summary counts",
    icon: "fa-solid fa-file-invoice",
  },
  {
    key: "payment",
    label: "Payment Status",
    description: "Payment breakdown totals",
    icon: "fa-solid fa-credit-card",
  },
  {
    key: "sparepart",
    label: "Spare Parts Status",
    description: "Spare part status totals",
    icon: "fa-solid fa-screwdriver-wrench",
  },
  {
    key: "jobsready",
    label: "Spare Parts Ready for Pickup",
    description: "Spare parts ready for pickup",
    icon: "fa-solid fa-box-open",
  },
  {
    key: "quotationrequested",
    label: "Quotations Awaiting Approval",
    description: "Quotations awaiting approval",
    icon: "fa-solid fa-paper-plane",
  },
];

const DEFAULT_EXPORT_OPTIONS = EXPORT_REPORTS.reduce((acc, r) => {
  acc[r.key] = true;
  return acc;
}, {});

// Memoized chart wrapper
const WorkOrderTrendChart = React.memo(({ data, options }) => (
  <Bar data={data} options={options} />
));

const Dashboard = () => {
  const navigate = useNavigate();

  // --- Shared dashboard filter ---
  const [dashboardFilter, setDashboardFilter] = React.useState("30-days");
  const customerListData = useSelector((state) => state.customer.customerList);



  // --- Single merged state for all dashboard data ---
  const [dashboardData, setDashboardData] = useState({
    jobStatus: [],
    paymentStatus: [],
    quotations: [],
    spareParts: [],
    workOrderCreatedLineChart: [],
    workOrderInvoicePaidLineChart: [],
    workOrderInvoiceNotPaidLineChart: [],
    workOrderRepairedLineChart: [],
    workOrderCreatedTotalCount: 0,
    workOrderInvoicePaidTotalCount: 0,
    workOrderInvoiceNotPaidTotalCount: 0,
    workOrderRepairedTotalCount: 0,
    quotationStatus: [],
    sparePartsStatus: [],
    odooSalesTotal: 0,
    odooQuotationTotal: 0,
  });
  const dispatch = useDispatch();

  // --- System Status data (Odoo Sync + Action Required) ---
  const [systemStatusData, setSystemStatusData] = useState({
    odooSummary: [],
    actionRequired: [],
  });
  // Main dashboard custom range
const [customStartDate, setCustomStartDate] = useState(null);
const [customEndDate, setCustomEndDate] = useState(null);

// Odoo card custom range
const [odooCustomStartDate, setOdooCustomStartDate] = useState(null);
const [odooCustomEndDate, setOdooCustomEndDate] = useState(null);

const isCustomRange = dashboardFilter === "CUSTOM";


  const [isLoading, setIsLoading] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportReportName, setExportReportName] = useState("Dashboard Reports");
  const [exportReportOptions, setExportReportOptions] = useState(
    DEFAULT_EXPORT_OPTIONS,
  );

  const [searchState, setSearchState] = useState({
    customer: null,
  });

  // =========================================================
  // ✅ SEPARATE filter + customer for Odoo Sales/Quotation card
  // =========================================================
  const [odooFilter, setOdooFilter] = useState("ALL");
  const [odooCustomer, setOdooCustomer] = useState(null);
const isOdooCustomRange = odooFilter === "CUSTOM";
  const currentFilterLabel = useMemo(() => {
  if (dashboardFilter === "CUSTOM") {
    if (customStartDate && customEndDate) {
      return `${moment(customStartDate).format("DD MMM YYYY")} → ${moment(
        customEndDate,
      ).format("DD MMM YYYY")}`;
    }
    return "Select a date range";
  }
  return filterOptions.find((f) => f.value === dashboardFilter)?.label || "";
}, [dashboardFilter, customStartDate, customEndDate]);

  // =========================================================
  // Fetch main dashboard data (excluding Odoo sales/quotation)
  // =========================================================
 const getDashboardData = async (rangeParams, customerID) => {
  setIsLoading(true);
  try {
    const params = { ...rangeParams };
    if (customerID) params.customerID = customerID;

      const [
        jobStatusRes,
        paymentStatusRes,
        quotationRes,
        sparePartsRes,
        workOrderLineChartRes,
        quotationStatusRes,
        sparePartsStatusRes,
      ] = await Promise.all([
        dashboardJobStatusCount(params),
        dashboardPaymentStatusCount(params),
        dashboardQuotationRequestedForApproval(params),
        dashboardSparePartsReadyForPickup(params),
        dashboardWorkOrderLineChart(params),
        dashboardQuotationStatusCount(params),
        dashboardSparePartsStatusCount(params),
      ]);

      const jobData = jobStatusRes?.data?.data || jobStatusRes?.data || [];
      const paymentData =
        paymentStatusRes?.data?.data || paymentStatusRes?.data || [];
      const quotationData =
        quotationRes?.data?.data || quotationRes?.data || [];
      const sparePartsList =
        sparePartsRes?.data?.data || sparePartsRes?.data || [];

      const lineChartResponse =
        workOrderLineChartRes?.data?.data || workOrderLineChartRes?.data || {};
      const createdLineChartData =
        lineChartResponse?.workOrderCreatedLineChart || [];
      const invoicePaidLineChartData =
        lineChartResponse?.workOrderInvoicePaidLineChart || [];
      const invoiceNotPaidChartData =
        lineChartResponse?.workOrderInvoiceNotPaidLineChart || [];
      const repairedLineChartData =
        lineChartResponse?.workOrderRepairedLineChart || [];
      const createdTotalCount = Number(
        lineChartResponse?.workOrderCreatedTotalCount ?? 0,
      );
      const invoicePaidTotalCount = Number(
        lineChartResponse?.workOrderInvoicePaidTotalCount ?? 0,
      );
      const invoiceNotPaidTotalCount = Number(
        lineChartResponse?.workOrderInvoiceNotPaidTotalCount ?? 0,
      );
      const repairedTotalCount = Number(
        lineChartResponse?.workOrderRepairedTotalCount ?? 0,
      );

      const quotationStatusData =
        quotationStatusRes?.data?.data || quotationStatusRes?.data || [];
      const sparePartsStatusData =
        sparePartsStatusRes?.data?.data || sparePartsStatusRes?.data || [];

      // ✅ Preserve previous odooSalesTotal / odooQuotationTotal
      setDashboardData((prev) => ({
        ...prev,
        jobStatus: Array.isArray(jobData) ? jobData : [],
        paymentStatus: Array.isArray(paymentData) ? paymentData : [],
        quotations: Array.isArray(quotationData) ? quotationData : [],
        spareParts: Array.isArray(sparePartsList) ? sparePartsList : [],
        workOrderCreatedLineChart: Array.isArray(createdLineChartData)
          ? createdLineChartData
          : [],
        workOrderInvoicePaidLineChart: Array.isArray(invoicePaidLineChartData)
          ? invoicePaidLineChartData
          : [],
        workOrderInvoiceNotPaidLineChart: Array.isArray(invoiceNotPaidChartData)
          ? invoiceNotPaidChartData
          : [],
        workOrderRepairedLineChart: Array.isArray(repairedLineChartData)
          ? repairedLineChartData
          : [],
        workOrderCreatedTotalCount: createdTotalCount,
        workOrderInvoicePaidTotalCount: invoicePaidTotalCount,
        workOrderInvoiceNotPaidTotalCount: invoiceNotPaidTotalCount,
        workOrderRepairedTotalCount: repairedTotalCount,
        quotationStatus: Array.isArray(quotationStatusData)
          ? quotationStatusData
          : [],
        sparePartsStatus: Array.isArray(sparePartsStatusData)
          ? sparePartsStatusData
          : [],
      }));
    } catch (error) {
      console.log("error ===>", error);
    } finally {
      setIsLoading(false);
    }
  };



  // =========================================================
  // ✅ Fetch Odoo Sales + Quotation counts (dedicated)
  // =========================================================
const getSalesAndQuotationCount = async (rangeParams, customerID) => {
  try {
    const params = { ...rangeParams };
    if (customerID) params.customerID = customerID;

      const [odooSalesRes, odooQuotationRes] = await Promise.all([
        odooSalesCount(params),
        odooQuotationCount(params),
      ]);

      const odooSalesData =
        odooSalesRes?.data?.data || odooSalesRes?.data || {};
      const odooSalesTotal = Number(odooSalesData?.totalSales ?? 0);

      const odooQuotationData =
        odooQuotationRes?.data?.data || odooQuotationRes?.data || {};
      const odooQuotationTotal = Number(
        odooQuotationData?.totalQuotations ??
        odooQuotationData?.totalCount ??
        odooQuotationData?.totalQuotation ??
        odooQuotationData?.totalSales ??
        odooQuotationData?.count ??
        0,
      );

      // ✅ CRITICAL FIX: spread previous state, don't overwrite it
      setDashboardData((prev) => ({
        ...prev,
        odooSalesTotal,
        odooQuotationTotal,
      }));
    } catch (error) {
      console.log("Odoo counts error ===>", error);
    }
  };

useEffect(() => {
  const customerID = searchState.customer?.ID || null;

  if (dashboardFilter === "CUSTOM") {
    // Require both dates before firing
    if (!customStartDate || !customEndDate) return;
    getDashboardData(
      {
        startDate: moment(customStartDate).format("YYYY-MM-DD"),
        endDate: moment(customEndDate).format("YYYY-MM-DD"),
      },
      customerID,
    );
  } else {
    const days =
      filterOptions.find((f) => f.value === dashboardFilter)?.days || 30;
    getDashboardData({ days }, customerID);
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [dashboardFilter, searchState.customer, customStartDate, customEndDate]);

 useEffect(() => {
  const customerID = odooCustomer?.ID || null;

  if (odooFilter === "CUSTOM") {
    if (!odooCustomStartDate || !odooCustomEndDate) return;
    getSalesAndQuotationCount(
      {
        startDate: moment(odooCustomStartDate).format("YYYY-MM-DD"),
        endDate: moment(odooCustomEndDate).format("YYYY-MM-DD"),
      },
      customerID,
    );
  } else if (odooFilter === "ALL") {
    getSalesAndQuotationCount({}, customerID);
  } else {
    const days = odooFilterOptions.find((f) => f.value === odooFilter)?.days;
    getSalesAndQuotationCount({ days }, customerID);
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [odooFilter, odooCustomer, odooCustomStartDate, odooCustomEndDate]);

  const getSystemStatusData = async () => {
    try {
      const [odooRes, actionRes] = await Promise.all([
        dashboardOdooSyncSummary(),
        dashboardActionRequired(),
      ]);

      const odooData = odooRes?.data?.data || odooRes?.data || [];
      const actionData = actionRes?.data?.data || actionRes?.data || [];

      setSystemStatusData({
        odooSummary: Array.isArray(odooData) ? odooData : [],
        actionRequired: Array.isArray(actionData) ? actionData : [],
      });
    } catch (error) {
      console.log("System status error ===>", error);
    }
  };

  useEffect(() => {
    let filterJSON = [
      {
        field: "UserStatus",
        condition: "eq",
        filterValues: ["ACTIVE"],
      },
    ];
    getSystemStatusData();
    getCustomerList(dispatch, { filters: JSON.stringify(filterJSON) });
  }, []);

  // Convenience aliases
  const jobStatusData = dashboardData.jobStatus;
  const paymentStatusData = dashboardData.paymentStatus;
  const quotationsData = dashboardData.quotations;
  const sparePartsData = dashboardData.spareParts;
  const workOrderCreatedLineChartData = dashboardData.workOrderCreatedLineChart;
  const workOrderInvoicePaidLineChartData =
    dashboardData.workOrderInvoicePaidLineChart;
  const workOrderInvoiceNotPaidLineChart =
    dashboardData.workOrderInvoiceNotPaidLineChart;
  const workOrderRepairedLineChart = dashboardData.workOrderRepairedLineChart;
  const workOrderCreatedTotalCount = dashboardData.workOrderCreatedTotalCount;
  const workOrderInvoicePaidTotalCount =
    dashboardData.workOrderInvoicePaidTotalCount;
  const workOrderInvoiceNotPaidTotalCount =
    dashboardData.workOrderInvoiceNotPaidTotalCount;
  const workOrderRepairedTotalCount = dashboardData.workOrderRepairedTotalCount;
  const quotationStatusData = dashboardData.quotationStatus;
  const sparePartsStatusData = dashboardData.sparePartsStatus;
  const odooSalesTotal = dashboardData.odooSalesTotal;
  const odooQuotationTotal = dashboardData.odooQuotationTotal;

  const odooSummary = systemStatusData.odooSummary;
  const actionRequired = systemStatusData.actionRequired;

  // =========================================================
  // JOB STATUS
  // =========================================================
  const jobStats = useMemo(() => {
    return (jobStatusData || []).map((item) => {
      const config = JOB_STATUS_CONFIG[item.jobStatus] || {
        label: item.jobStatus,
        color: "#9ca3af",
      };
      return {
        label: config.label,
        value: item.totalCount,
        color: config.color,
      };
    });
  }, [jobStatusData]);

  const totalJobs = useMemo(
    () => jobStats.reduce((acc, s) => acc + s.value, 0),
    [jobStats],
  );

  const jobDoughnutData = useMemo(
    () => ({
      labels: jobStats.map((s) => s.label),
      datasets: [
        {
          data: jobStats.map((s) => s.value),
          backgroundColor: jobStats.map((s) => s.color),
          borderWidth: 2,
          borderColor: "#fff",
          hoverOffset: 20,
          hoverBorderWidth: 3,
          hoverBorderColor: "#fff",
        },
      ],
    }),
    [jobStats],
  );

  const jobDoughnutOptions = useMemo(
    () => ({
      cutout: "72%",
      maintainAspectRatio: false,
      layout: { padding: 12 },
      plugins: {
        legend: { display: false },
        tooltip: {
          position: "nearest",
          caretPadding: 12,
          backgroundColor: "#fff",
          titleColor: "#1f2937",
          bodyColor: "#6b7280",
          borderColor: "#e3e8ef",
          borderWidth: 1,
          padding: 10,
          displayColors: true,
          callbacks: {
            label: (ctx) => ` ${ctx.label}: ${ctx.raw}`,
          },
        },
      },
      animation: { duration: 500 },
    }),
    [],
  );

  // =========================================================
  // PAYMENT STATUS
  // =========================================================
  const paymentStats = useMemo(() => {
    const stats = { successful: 0, pending: 0, failed: 0, cancelled: 0 };
    (paymentStatusData || []).forEach((item) => {
      const status = item.paymentStatus?.toUpperCase();
      if (status === "SUCCESS") stats.successful = item.totalCount;
      else if (status === "PENDING") stats.pending = item.totalCount;
      else if (status === "FAILED") stats.failed = item.totalCount;
      else if (status === "CANCELLED") stats.cancelled = item.totalCount;
    });
    return stats;
  }, [paymentStatusData]);

  const paymentTotal =
    paymentStats.successful +
    paymentStats.pending +
    paymentStats.failed +
    paymentStats.cancelled;

  const paymentDoughnutData = useMemo(
    () => ({
      labels: ["Successful", "Pending", "Failed", "Cancelled"],
      datasets: [
        {
          data: [
            paymentStats.successful,
            paymentStats.pending,
            paymentStats.failed,
            paymentStats.cancelled,
          ],
          backgroundColor: ["#10b981", "#fbbf24", "#ef4444", "#9ca3af"],
          borderWidth: 2,
          borderColor: "#fff",
          hoverOffset: 20,
          hoverBorderWidth: 3,
          hoverBorderColor: "#fff",
        },
      ],
    }),
    [paymentStats],
  );

  const paymentDoughnutOptions = useMemo(
    () => ({
      cutout: "72%",
      maintainAspectRatio: false,
      layout: { padding: 12 },
      plugins: {
        legend: { display: false },
        tooltip: {
          position: "nearest",
          caretPadding: 12,
          backgroundColor: "#fff",
          titleColor: "#1f2937",
          bodyColor: "#6b7280",
          borderColor: "#e3e8ef",
          borderWidth: 1,
          padding: 10,
          displayColors: true,
          callbacks: {
            label: (ctx) => ` ${ctx.label}: ${ctx.raw}`,
          },
        },
      },
      animation: { duration: 500 },
    }),
    [],
  );

  // =========================================================
  // QUOTATION STATUS
  // =========================================================
  const quotationStats = useMemo(() => {
    return (quotationStatusData || []).map((item) => {
      const config = QUOTATION_STATUS_CONFIG[item.quotationStatus] || {
        label: item.quotationStatus,
        color: "#9ca3af",
      };
      return {
        label: config.label,
        value: item.totalCount,
        color: config.color,
      };
    });
  }, [quotationStatusData]);

  const totalQuotations = useMemo(
    () => quotationStats.reduce((acc, s) => acc + s.value, 0),
    [quotationStats],
  );

  const quotationDoughnutData = useMemo(
    () => ({
      labels: quotationStats.map((s) => s.label),
      datasets: [
        {
          data: quotationStats.map((s) => s.value),
          backgroundColor: quotationStats.map((s) => s.color),
          borderWidth: 2,
          borderColor: "#fff",
          hoverOffset: 20,
          hoverBorderWidth: 3,
          hoverBorderColor: "#fff",
        },
      ],
    }),
    [quotationStats],
  );

  // =========================================================
  // SPARE PART STATUS
  // =========================================================
  const sparePartsStatusStats = useMemo(() => {
    return (sparePartsStatusData || []).map((item) => {
      const config = SPARE_PART_STATUS_CONFIG[item.sparePartStatus] || {
        label: item.sparePartStatus,
        color: "#9ca3af",
      };
      return {
        label: config.label,
        value: item.totalCount,
        color: config.color,
      };
    });
  }, [sparePartsStatusData]);

  const totalSparePartsStatus = useMemo(
    () => sparePartsStatusStats.reduce((acc, s) => acc + s.value, 0),
    [sparePartsStatusStats],
  );

  const sparePartsStatusDoughnutData = useMemo(
    () => ({
      labels: sparePartsStatusStats.map((s) => s.label),
      datasets: [
        {
          data: sparePartsStatusStats.map((s) => s.value),
          backgroundColor: sparePartsStatusStats.map((s) => s.color),
          borderWidth: 2,
          borderColor: "#fff",
          hoverOffset: 20,
          hoverBorderWidth: 3,
          hoverBorderColor: "#fff",
        },
      ],
    }),
    [sparePartsStatusStats],
  );

  // =========================================================
  // SPARE PARTS CARD
  // =========================================================
  const sparePartsStats = useMemo(() => {
    return {
      total: sparePartsData.length,
      readyToday: sparePartsData.filter((item) => {
        const today = new Date().toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
        return item.updatedAt?.includes(today);
      }).length,
    };
  }, [sparePartsData]);

  const formatReadySince = (dateStr) => {
    if (!dateStr) return "-";
    return dateStr.replace(/\s\d{4}\s/, " ");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
    }
  };

  // =========================================================
  // ODOO INTEGRATION MODULES
  // =========================================================
  const odooModules = useMemo(() => {
    const lookup = {};
    odooSummary.forEach((item) => {
      lookup[item.moduleName] = item;
    });

    return ODOO_MODULE_ORDER.map((moduleKey) => {
      const item = lookup[moduleKey] || {
        moduleName: moduleKey,
        syncCount: 0,
        notSyncCount: 0,
      };
      const total = item.syncCount + item.notSyncCount;
      const syncPct = total > 0 ? (item.syncCount / total) * 100 : 0;
      return {
        moduleKey,
        label: ODOO_MODULE_LABELS[moduleKey] || moduleKey,
        syncCount: item.syncCount,
        notSyncCount: item.notSyncCount,
        syncPct: parseFloat(syncPct.toFixed(1)),
        isHealthy: item.notSyncCount === 0,
      };
    });
  }, [odooSummary]);

  // =========================================================
  // WORK ORDER TREND
  // =========================================================

  const workOrderTrendData = useMemo(() => {
    const isLongTerm =
      dashboardFilter === "6 Months" || dashboardFilter === "12 Months";

    // ✅ Guard each dataset so .map/.slice don't crash and lengths stay aligned
    const createdData = Array.isArray(workOrderCreatedLineChartData)
      ? workOrderCreatedLineChartData
      : [];
    const invoicePaidData = Array.isArray(workOrderInvoicePaidLineChartData)
      ? workOrderInvoicePaidLineChartData
      : [];
    const invoiceNotPaidData = Array.isArray(workOrderInvoiceNotPaidLineChart)
      ? workOrderInvoiceNotPaidLineChart
      : [];
    const repairedData = Array.isArray(workOrderRepairedLineChart)
      ? workOrderRepairedLineChart
      : [];

    const aggregateData = (dataArray) => {
      const labels = [];
      const values = [];

      if (!dataArray || dataArray.length === 0) return { labels, values };

      if (isLongTerm) {
        const monthlyMap = new Map();
        dataArray.forEach((item) => {
          const parts = (item.date || "").split(" ");
          if (parts.length === 3) {
            const monthYear = `${parts[1]} ${parts[2]}`;
            monthlyMap.set(
              monthYear,
              (monthlyMap.get(monthYear) || 0) + (item.totalCount || 0),
            );
          }
        });
        monthlyMap.forEach((total, monthYear) => {
          labels.push(monthYear);
          values.push(total);
        });
      } else {
        dataArray.forEach((item) => {
          labels.push((item.date || "").replace(/\s\d{4}$/, ""));
          values.push(item.totalCount ?? 0);
        });
      }
      return { labels, values };
    };

    const createdResult = aggregateData(createdData);
    const invoicePaidResult = aggregateData(invoicePaidData);
    const invoiceNotPaidResult = aggregateData(invoiceNotPaidData);
    const repairedResult = aggregateData(repairedData);

    // Keep every calendar day for 7/15/30/60/90-day ranges. Use the
    // created series' dates as the shared x-axis and match other series by
    // date so a missing zero-count day cannot shift subsequent values.
    const labels = createdResult.labels;
    const alignByDate = (dataArray) => {
      const countsByDate = new Map(
        dataArray.map((item) => [item.date || "", Number(item.totalCount) || 0]),
      );
      return createdData.map((item) =>
        countsByDate.get(item.date || "") ?? 0,
      );
    };
    const alignMonthly = (values) =>
      labels.map((_, index) => values[index] ?? 0);

    const createdValues = createdResult.values;
    const invoiceValues = isLongTerm
      ? alignMonthly(invoicePaidResult.values)
      : alignByDate(invoicePaidData);
    const invoiceNotPaidValues = isLongTerm
      ? alignMonthly(invoiceNotPaidResult.values)
      : alignByDate(invoiceNotPaidData);
    const repairedValues = isLongTerm
      ? alignMonthly(repairedResult.values)
      : alignByDate(repairedData);



    return {
      data: {
        labels,
        datasets: [
          {
            label: `Total Work Orders (${workOrderCreatedTotalCount})`,
            backgroundColor: "#6366F1",
            borderColor: "#6366F1",
            borderWidth: 1,
            borderRadius: 4,
            maxBarThickness: 28,
            data: createdValues,
          },
          {
            label: `Invoice Paid (${workOrderInvoicePaidTotalCount})`,
            backgroundColor: "#10B981",
            borderColor: "#10B981",
            borderWidth: 1,
            borderRadius: 4,
            maxBarThickness: 28,
            data: invoiceValues,
          },
          {
            label: `Invoice Not Paid (${workOrderInvoiceNotPaidTotalCount})`,
            backgroundColor: "#EF4444",
            borderColor: "#EF4444",
            borderWidth: 1,
            borderRadius: 4,
            maxBarThickness: 28,
            data: invoiceNotPaidValues,
          },
          {
            label: `Repaired (${workOrderRepairedTotalCount})`,
            backgroundColor: "#0EA5E9",
            borderColor: "#0EA5E9",
            borderWidth: 1,
            borderRadius: 4,
            maxBarThickness: 28,
            data: repairedValues,
          },
        ],
      },
      options: {
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: "top",
            align: "center",
            labels: {
              usePointStyle: true,
              boxWidth: 12,
              boxHeight: 12,
              padding: 16,
              color: "#6b7280",
              font: {
                size: 12,
                family: "'Helvetica Neue', Helvetica, Arial, sans-serif",
                weight: "500",
              },
            },
          },
          tooltip: {
            backgroundColor: "#fff",
            titleColor: "#1f2937",
            bodyColor: "#6b7280",
            borderColor: "#e3e8ef",
            borderWidth: 1,
            padding: 12,
            displayColors: true,
            mode: "index",
            intersect: false,
            callbacks: {
              title: (items) => {
                const idx = items[0]?.dataIndex ?? 0;
                if (isLongTerm) return labels[idx];
                return createdData[idx]?.date || "";
              },
              label: (item) => {
                const suffix = isLongTerm ? " (Month)" : "";
                return ` ${item.dataset.label}: ${item.formattedValue}${suffix}`;
              },
            },
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            grid: { color: "#f3f4f6", drawBorder: true, drawOnChartArea: true },
            ticks: {
              color: "#9ca3af",
              font: { size: 10 },
              padding: 10,
              stepSize: 1,
              precision: 0,
            },
          },
          x: {
            grid: {
              display: true,
              color: "#f3f4f6",
              drawBorder: true,
              drawOnChartArea: true,
            },
            ticks: {
              color: "#9ca3af",
              font: { size: 10 },
              padding: 10,
              autoSkip: true,
              maxTicksLimit: 12,
              maxRotation: 0,
              minRotation: 0,
            },
          },
        },
      },
    };
    // ✅ All four data sources are dependencies now
  }, [
    workOrderCreatedLineChartData,
    workOrderInvoicePaidLineChartData,
    workOrderInvoiceNotPaidLineChart,
    workOrderRepairedLineChart,
    workOrderCreatedTotalCount,
    workOrderInvoicePaidTotalCount,
    workOrderInvoiceNotPaidTotalCount,
    workOrderRepairedTotalCount,
    dashboardFilter,
  ]);


  const openInNewTab = (path) => {
    window.open(path, "_blank", "noopener,noreferrer");
  };

  // =========================================================
  // EXPORT MODAL HELPERS
  // =========================================================
  const toggleExportReport = (reportKey) => {
    console.log("clicked on checkbox", reportKey)
    setExportReportOptions((prev) => ({
      ...prev,
      [reportKey]: !prev[reportKey],
    }));
  };

  const allReportsSelected = EXPORT_REPORTS.every(
    (r) => exportReportOptions[r.key],
  );
  const selectedCount = EXPORT_REPORTS.filter(
    (r) => exportReportOptions[r.key],
  ).length;

  const handleToggleSelectAll = () => {
    const nextValue = !allReportsSelected;
    const next = {};
    EXPORT_REPORTS.forEach((r) => {
      next[r.key] = nextValue;
    });
    setExportReportOptions(next);
  };

  const handleCloseExportModal = () => {
    setIsExportModalOpen(false);
    setExportReportName("Dashboard Reports");
    setExportReportOptions(DEFAULT_EXPORT_OPTIONS);
  };

  const handleDashboardExport = async () => {
    const selectedReports = EXPORT_REPORTS.filter(
      (r) => exportReportOptions[r.key],
    ).map((r) => r.key);

    if (!selectedReports.length) {
      alert("Please select at least one report to export.");
      return;
    }

    setIsExporting(true);

    try {
      const selectedDays =
        filterOptions.find((item) => item.value === dashboardFilter)?.days || 30;

      const response = await downloadCsv({
        reportName: selectedReports.join(","),
        days: selectedDays,
        ...(searchState.customer?.ID
          ? { customerID: searchState.customer.ID }
          : {}),
      });

      if (response?.status === 200) {
        const blob = new Blob(["\uFEFF" + response.data], {
          type: "text/csv;charset=utf-8;",
        });
        const fileUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");

        link.href = fileUrl;
        link.download = `dashboard_reports_${new Date()
          .toISOString()
          .slice(0, 10)}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(fileUrl);

        handleCloseExportModal();
      } else {
        const message =
          response?.message ||
          response?.data?.message ||
          "Failed to export dashboard report.";
        console.error(message);
      }
    } catch (error) {
      console.error("Dashboard export failed:", error);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      {/* =========================================================
          EXPORT MODAL
          ========================================================= */}
      <Modal
        isOpen={isExportModalOpen}
        toggle={handleCloseExportModal}
        backdrop="static"
        className="dashboard-export-modal"
        size="xl"
      >
        <ModalHeader toggle={handleCloseExportModal}>
          <div className="dashboard-export-modal-title">

            <div>
              <h2 className="mb-2">Export Dashboard Report</h2>
              <p className="mb-0">
                Select the reports you want to include in the export
              </p>
            </div>
          </div>
        </ModalHeader>

        <ModalBody className="dashboard-export-modal-body">
          <div className="dashboard-export-section">
            <div className="dashboard-export-section-header">
              <div className="dashboard-export-section-title">

                <span className="dashboard-export-count">
                  {selectedCount} Reports selected
                </span>
              </div>
              <div className="dashboard-export-bulk-actions">
                <Button
                  color="blue"
                  type="button"
                  className="dashboard-export-bulk-btn"
                  onClick={handleToggleSelectAll}
                >
                  {allReportsSelected ? "Clear all" : "Select all"}
                </Button>
              </div>
            </div>

            <div className="dashboard-export-options">
              {EXPORT_REPORTS.map((option) => {
                const isSelected = !!exportReportOptions[option.key];
                return (
                  <div
                    key={option.key}
                    className={`dashboard-export-option ${isSelected ? "is-selected" : ""
                      }`}
                    onClick={() => toggleExportReport(option.key)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        toggleExportReport(option.key);
                      }
                    }}
                  >
                    <div className="dashboard-export-option-icon">
                      <i className={option.icon} />
                    </div>
                    <div className="dashboard-export-option-copy">
                      <div className="dashboard-export-option-title">
                        {option.label}
                      </div>
                      <div className="dashboard-export-option-text">
                        {option.description}
                      </div>
                    </div>
                    <div className="dashboard-export-option-check">
                      <Input
                        type="checkbox"
                        checked={isSelected}


                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </ModalBody>

        <ModalFooter>
          <Button
            color="secondary"
            className="dashboard-export-btn-cancel"
            onClick={handleCloseExportModal}
            disabled={isExporting}
          >
            Cancel
          </Button>
          <Button
            color="blue"
            onClick={handleDashboardExport}
            disabled={isExporting}
          >
            {isExporting ? (
              <>
                <span className="dashboard-export-spinner" />
                Exporting...
              </>
            ) : (
              <>
                <i className="fa-solid fa-download mr-1" />
                Export CSV
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>

      <Container fluid>
        <div className="odoo-dashboard-wrapper">
          <div className="section-breaker">
            <div className="section-breaker-line"></div>
            <div className="section-breaker-label">
              <span className="section-breaker-icon">◈</span>
              <span>Operations Overview</span>
            </div>
            <div className="section-breaker-line"></div>
          </div>

          {/* TOP TOOLBAR: Filter + Customer + Export */}
          <Row className="dashboard-top-controls-row">
            <Col xs="12">
              <div className="dashboard-toolbar">
                <div className="dashboard-toolbar-left">
                  <div className="wot-filter-wrapper">
                    <i className="fa-solid fa-calendar-day wot-filter-icon" />
                    <Select
                      className="wot-days-select"
                      classNamePrefix="react-select"
                      options={filterOptions}
                      value={filterOptions.find((option) => option.value === dashboardFilter)}
                      onChange={(option) => setDashboardFilter(option.value)}
                      getOptionValue={(option) => option.value}
                      isSearchable={false}
                      menuPlacement="auto"
                      menuPosition="fixed"
                      menuPortalTarget={document.body}
                      styles={daysSelectStyles}
                    />
                    <i className="fa-solid fa-chevron-down wot-filter-caret" />
                  </div>

                   {isCustomRange && (
    <div className="dashboard-custom-range">
      <div className="custom-date-field">
        <i className="fa-solid fa-calendar wot-filter-icon" />
        <DatePicker
          selected={customStartDate}
          onChange={(date) => setCustomStartDate(date)}
          selectsStart
          startDate={customStartDate}
          endDate={customEndDate}
          maxDate={customEndDate || new Date()}
          placeholderText="Start date"
          className="form-control custom-date-input"
          dateFormat="dd-MM-yyyy"
          isClearable
        />
      </div>

      <div className="custom-date-field">
        <i className="fa-solid fa-calendar wot-filter-icon" />
        <DatePicker
          selected={customEndDate}
          onChange={(date) => setCustomEndDate(date)}
          selectsEnd
          startDate={customStartDate}
          endDate={customEndDate}
          minDate={customStartDate}
          maxDate={new Date()}
          placeholderText="End date"
          className="form-control custom-date-input"
          dateFormat="dd-MM-yyyy"
          isClearable
        />
      </div>
      <Button
        type="button"
        color="default"
        className="d-flex justify-content-center align-items-center mt-0 mb-0 ml-2 filter-btn btn-simple"
        onClick={() => {
          setCustomStartDate(null);
          setCustomEndDate(null);
          setDashboardFilter("30-days");
        }}
      >
        <><i className="fa fa-times mr-1" /> Clear</>
      </Button>
    </div>
  )}

                  <div className="dashboard-customer-select">
                    <Select
                      className="filter-select"
                      options={customerListData?.data?.customer}
                      getOptionLabel={(option) =>
                        `${option?.firstName || ""} ${option?.lastName || ""}`.trim() ||
                        `${option?.companyName}`.trim()
                      }
                      getOptionValue={(option) => option.ID}
                      value={searchState.customer}
                      placeholder="Customer"
                      classNamePrefix="react-select"
                      styles={customerSelectStyles}

                      isClearable
                      onChange={(selected) =>
                        setSearchState((prev) => ({
                          ...prev,
                          customer: selected
                            ? {
                              ID: selected.ID,
                              userID: selected.userID,
                              firstName: selected?.firstName,
                              lastName: selected?.lastName,
                              companyName: selected?.companyName,
                            }
                            : null,
                        }))
                      }
                      onKeyDown={handleKeyDown}
                    />
                  </div>
                   {/* ✅ Custom date range */}
                </div>

                <Button
                  color="blue"
                  className="dashboard-export-btn"
                  onClick={() => setIsExportModalOpen(true)}
                  disabled={isExporting}
                >
                  <i className="fas fa-arrow-up-from-bracket mr-2" />
                  Export Report
                </Button>
              </div>
            </Col>

            {/* WORK ORDER TREND */}
            <Col xs="12">
              <Card className="work-order-trend-card">
                <CardBody>
                  <div className="wot-header">
                    <div>
                      <h5 className="wot-title">Work Order Trend</h5>
                      <p className="wot-subtitle">{currentFilterLabel}</p>
                    </div>
                  </div>
                  <div className="wot-chart-area">
                    {workOrderCreatedLineChartData.length === 0 ? (
                      <div className="text-center text-muted py-5">
                        {isLoading
                          ? "Loading chart..."
                          : "No work order data for this period"}
                      </div>
                    ) : (
                      <WorkOrderTrendChart
                        data={workOrderTrendData.data}
                        options={workOrderTrendData.options}
                      />
                    )}
                  </div>
                </CardBody>
              </Card>
            </Col>

            {/* JOB STATUS CARD */}
            <Col lg="6" md="6" xl="3" sm="12">
              <Card className="job-status-card">
                <CardBody>
                  <div className="job-header">
                    <div>
                      <h5 className="dashboard-job-title">Work Order Status</h5>
                      <p className="job-subtitle">
                        {currentFilterLabel} Created Work Orders • {totalJobs}{" "}
                        total
                      </p>
                    </div>
                  </div>

                  <div className="job-content">
                    <div className="chart-container">
                      <Doughnut
                        data={jobDoughnutData}
                        options={jobDoughnutOptions}
                      />
                      <div className="chart-inner-circle">
                        <span className="chart-total">{totalJobs}</span>
                        <span className="chart-label">Work Orders</span>
                      </div>
                    </div>

                   <div className="legend-container">
  {jobStats.map((item, index) => (
    <div key={index} className="legend-item">
      <div className="legend-left">
        <span
          className="legend-dot"
          style={{ backgroundColor: item.color }}
        ></span>
        <span className="legend-text" title={item.label}>
          {item.label}
        </span>
      </div>
      <span className="legend-value">{item.value}</span>
    </div>
  ))}
</div>
                  </div>

                  <div className="qs-footer">
                    <div />
                    <div className="qs-header-right">
                      <span
                        className="qs-review-link"
                        onClick={() => openInNewTab("/admin/jobs")}
                      >
                        Details →
                      </span>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>

            {/* QUOTATION STATUS CARD */}
            <Col lg="6" md="6" xl="3" sm="12">
              <Card className="job-status-card">
                <CardBody>
                  <div className="job-header">
                    <div>
                      <h5 className="dashboard-job-title">Quotation Status</h5>
                      <p className="job-subtitle">
                        {currentFilterLabel} Quotations • {totalQuotations} total
                      </p>
                    </div>
                  </div>

                  <div className="job-content">
                    <div className="chart-container">
                      <Doughnut
                        data={quotationDoughnutData}
                        options={jobDoughnutOptions}
                      />
                      <div className="chart-inner-circle">
                        <span className="chart-total">{totalQuotations}</span>
                        <span className="chart-label">Quotations</span>
                      </div>
                    </div>

                   <div className="legend-container">
  {quotationStats.map((item, index) => (
    <div key={index} className="legend-item">
      <div className="legend-left">
        <span
          className="legend-dot"
          style={{ backgroundColor: item.color }}
        ></span>
        <span className="legend-text" title={item.label}>
          {item.label}
        </span>
      </div>
      <span className="legend-value">{item.value}</span>
    </div>
  ))}
</div>
                  </div>

                  <div className="qs-footer">
                    <div />
                    <div className="qs-header-right">
                      <span
                        className="qs-review-link"
                        onClick={() => openInNewTab("/admin/jobs")}
                      >
                        Details →
                      </span>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>

            {/* PAYMENT STATUS CARD */}
            <Col lg="6" md="6" xl="3" sm="12">
              <Card className="payment-status-card">
                <CardBody>
                  <div className="ps-header">
                    <div>
                      <h5 className="ps-title">Payment Status</h5>
                      <p className="ps-subtitle">
                        {currentFilterLabel} Initiated Payments • Cash customers
                      </p>
                    </div>
                  </div>

                  <div className="ps-content">
                    <div className="ps-chart-container">
                      <Doughnut
                        data={paymentDoughnutData}
                        options={paymentDoughnutOptions}
                      />
                      <div className="ps-chart-inner">
                        <span className="ps-chart-total">{paymentTotal}</span>
                        <span className="ps-chart-label">Payments</span>
                      </div>
                    </div>

                    <div className="ps-legend">
                      {paymentStats.successful > 0 && (
                        <div className="ps-legend-item">
                          <div className="ps-legend-left">
                            <span
                              className="ps-legend-dot"
                              style={{ background: "#10b981" }}
                            ></span>
                            <span className="legend-text" title="Successful">Successful</span>
                          </div>
                          <span className="ps-legend-value">
                            {paymentStats.successful}
                          </span>
                        </div>
                      )}
                      {paymentStats.pending > 0 && (

                        <div className="ps-legend-item">
                          <div className="ps-legend-left">
                            <span
                              className="ps-legend-dot"
                              style={{ background: "#fbbf24" }}
                            ></span>
                            <span className="legend-text"  title="Pending">Pending</span>
                          </div>
                          <span className="ps-legend-value">
                            {paymentStats.pending}
                          </span>
                        </div>
                      )}
                      {paymentStats.failed > 0 && (

                        <div className="ps-legend-item">
                          <div className="ps-legend-left">
                            <span
                              className="ps-legend-dot"
                              style={{ background: "#ef4444" }}
                            ></span>
                            <span className="legend-text " title="Failed">Failed</span>
                          </div>
                          <span className="ps-legend-value">
                            {paymentStats.failed}
                          </span>
                        </div>
                      )}
                      {paymentStats.cancelled > 0 && (

                        <div className="ps-legend-item">
                          <div className="ps-legend-left">
                            <span
                              className="ps-legend-dot"
                              style={{ background: "#9ca3af" }}
                            ></span>
                            <span className="legend-text" title="Cancelled">Cancelled</span>
                          </div>
                          <span className="ps-legend-value">
                            {paymentStats.cancelled}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="qs-footer">
                    <div />
                    <div className="qs-header-right">
                      <span
                        className="qs-review-link"
                        onClick={() => openInNewTab("/admin/payments")}
                      >
                        Details →
                      </span>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>

            {/* SPARE PARTS STATUS CARD */}
            <Col lg="6" md="6" xl="3" sm="12">
              <Card className="payment-status-card">
                <CardBody>
                  <div className="ps-header">
                    <div>
                      <h5 className="ps-title">Spare Parts Status</h5>
                      <p className="ps-subtitle">
                        {currentFilterLabel} Requests • {totalSparePartsStatus}{" "}
                        total
                      </p>
                    </div>
                  </div>

                  <div className="ps-content">
                    <div className="ps-chart-container">
                      <Doughnut
                        data={sparePartsStatusDoughnutData}
                        options={paymentDoughnutOptions}
                      />
                      <div className="ps-chart-inner">
                        <span className="ps-chart-total">
                          {totalSparePartsStatus}
                        </span>
                        <span className="ps-chart-label">Spare Parts</span>
                      </div>
                    </div>

                    <div className="ps-legend">
                      {sparePartsStatusStats.map((item, index) => (
                        <div key={index} className="ps-legend-item">
                          <div className="ps-legend-left">
                            <span
                              className="ps-legend-dot"
                              style={{ background: item.color }}
                            ></span>
                            <span className="legend-text" title={item.label}>
                              {item.label}
                            </span>
                          </div>
                          <span className="ps-legend-value">{item.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="qs-footer">
                    <div />
                    <div className="qs-header-right">
                      <span
                        className="qs-review-link"
                        onClick={() => openInNewTab("/admin/parts-requested")}
                      >
                        Details →
                      </span>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* SPARE PARTS READY + QUOTATIONS AWAITING */}
          <Row>
            <Col lg="6">
              <Card className="quotation-status-card">
                <CardBody>
                  <div className="qs-header">
                    <div>
                      <h5 className="qs-title">Spare Parts Ready for Pickup</h5>
                      <p className="qs-subtitle">
                        {currentFilterLabel} • Technicians need to collect
                      </p>
                    </div>
                  </div>

                  <div className="qs-scroll-area">
                    <div className="qs-list">
                      <div className="qs-list-header">
                        <span>Work Order</span>
                        <span>Company Name</span>
                        <span>Customer</span>
                        <span>Technician</span>
                        <span className="text-right">Ready Since</span>
                      </div>

                      {sparePartsData.map((item, idx) => (
                        <div
                          className="qs-item"
                          key={`${item.workOrderID}-${idx}`}
                        >
                          <div className="qs-col qs-col-id">
                            <span className="qs-item-id">
                              {item.workOrderID}
                            </span>
                          </div>
                          <div className="qs-col qs-col-customer">
                            <span className="qs-item-price">
                              {item.companyName || "-"}
                            </span>
                          </div>
                          <div className="qs-col qs-col-customer">
                            <span className="qs-item-price">
                              {item.customerFirstName || "-"}{" "}
                              {item?.customerLastName}
                            </span>
                          </div>
                          <div className="qs-col qs-col-tech">
                            <span className="qs-item-price">
                              {item.firstName} {item.lastName}
                            </span>
                          </div>
                          <div className="qs-col qs-col-date text-right">
                            <span className="qs-item-price">
                              {formatReadySince(item.updatedAt)}
                            </span>
                          </div>
                        </div>
                      ))}
                      {sparePartsData.length === 0 && (
                        <div className="qs-item qs-empty">
                          <span className="qs-item-desc">
                            No spare parts ready for pickup
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="qs-footer">
                    <span>
                      Total: {sparePartsStats.total} work order(s) ready
                    </span>
                    <div className="qs-header-right">
                      <span
                        className="qs-review-link"
                        onClick={() =>
                          openInNewTab(
                            "/admin/parts-requested?defaultTab=READY_FOR_PICKUP",
                          )
                        }
                      >
                        Details →
                      </span>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col lg="6" md="12">
              <Card className="quotation-status-card">
                <CardBody>
                  <div className="qs-header">
                    <div>
                      <h5 className="qs-title">Quotations Awaiting Approval</h5>
                      <p className="qs-subtitle">{currentFilterLabel}</p>
                    </div>
                  </div>

                  <div className="qs-scroll-area">
                    <div className="qs-list">
                      <div className="qs-list-header">
                        <span>Work Order</span>
                        <span>Company Name</span>
                        <span>Customer</span>
                        <span>Awaiting Since</span>
                        <span className="text-right">Amount</span>
                      </div>

                      {quotationsData.map((item, idx) => (
                        <div
                          className="qs-item"
                          key={`${item.workOrderID}-${idx}`}
                        >
                          <div className="qs-col qs-col-id">
                            <span className="qs-item-id">
                              {item.workOrderID}
                            </span>
                          </div>
                          <div className="qs-col qs-col-customer">
                            <span className="qs-item-price">
                              {item.companyName?.trim() ||
                                `${item.firstName || ""} ${item.lastName || ""}`.trim() ||
                                "-"}
                            </span>
                          </div>
                          <div className="qs-col qs-col-customer">
                            <span className="qs-item-price">
                              {item.firstName || "-"} {item.lastName || ""}
                            </span>
                          </div>
                          <div className="qs-col qs-col-customer">
                            <span className="qs-item-price">
                              {item?.createdAt?.trim() ||
                                `${item?.createdAt || ""}`.trim() ||
                                "-"}
                            </span>
                          </div>
                          <div className="qs-col qs-col-amount text-right">
                            <span className="qs-item-price">
                              SAR {item.grandTotal?.toLocaleString()}
                            <span className="qs-item-tag ml-2">
                              {item.paymentTerm}
                            </span>
                            </span>
                          </div>
                        </div>
                      ))}
                      {quotationsData.length === 0 && (
                        <div className="qs-item qs-empty">
                          <span className="qs-item-desc">
                            No pending quotations
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="qs-footer">
                    <div />
                    <div className="qs-header-right">
                      <span
                        className="qs-review-link"
                        onClick={() =>
                          openInNewTab(
                            "/admin/jobs?defaultStatus=REQUEST_APPROVAL",
                          )
                        }
                      >
                        Details →
                      </span>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* SECTION BREAKER */}
          <div className="section-breaker">
            <div className="section-breaker-line"></div>
            <div className="section-breaker-label">
              <span className="section-breaker-icon">◈</span>
              <span>Odoo Sales and Quotation Count | Odoo Integration</span>
            </div>
            <div className="section-breaker-line"></div>
          </div>

          {/* ODOO INTEGRATION + ODOO SALES/QUOTATION */}
          <div className="dashboard-toolbar odoo-summary-toolbar">
            <div className="dashboard-toolbar-left">
              <div className="wot-filter-wrapper">
                <i className="fa-solid fa-calendar-day wot-filter-icon" />
                <Select
                  className="wot-days-select"
                  classNamePrefix="react-select"
                  options={odooFilterOptions}
                  value={odooFilterOptions.find((option) => option.value === odooFilter)}
                  onChange={(option) => setOdooFilter(option.value)}
                  getOptionValue={(option) => option.value}
                  isSearchable={false}
                  menuPlacement="auto"
                  menuPosition="fixed"
                  menuPortalTarget={document.body}
                  styles={daysSelectStyles}
                />
                <i className="fa-solid fa-chevron-down wot-filter-caret" />
              </div>

              {isOdooCustomRange && (
                <div className="dashboard-custom-range">
                  <div className="custom-date-field">
                    <i className="fa-solid fa-calendar wot-filter-icon" />
                    <DatePicker
                      selected={odooCustomStartDate}
                      onChange={(date) => setOdooCustomStartDate(date)}
                      selectsStart
                      startDate={odooCustomStartDate}
                      endDate={odooCustomEndDate}
                      maxDate={odooCustomEndDate || new Date()}
                      placeholderText="Start date"
                      className="form-control custom-date-input"
                      dateFormat="dd-MM-yyyy"
                      isClearable
                    />
                  </div>
                  <div className="custom-date-field">
                    <i className="fa-solid fa-calendar wot-filter-icon" />
                    <DatePicker
                      selected={odooCustomEndDate}
                      onChange={(date) => setOdooCustomEndDate(date)}
                      selectsEnd
                      startDate={odooCustomStartDate}
                      endDate={odooCustomEndDate}
                      minDate={odooCustomStartDate}
                      maxDate={new Date()}
                      placeholderText="End date"
                      className="form-control custom-date-input"
                      dateFormat="dd-MM-yyyy"
                      isClearable
                    />
                  </div>
                  <Button
                    type="button"
                    color="default"
                    className="d-flex justify-content-center align-items-center mt-0 mb-0 ml-2 filter-btn btn-simple"
                    onClick={() => {
                      setOdooCustomStartDate(null);
                      setOdooCustomEndDate(null);
                      setOdooFilter("ALL");
                    }}
                  >
                    <><i className="fa fa-times mr-1" /> Clear</>
                  </Button>
                </div>
              )}

           
            </div>
          </div>

          <Row className="odoo-summary-row align-items-stretch">
            {/* ODOO INTEGRATION CARD */}
            <Col lg="8" className="order-2">
              <Card className="odoo-integration-card">
                <CardBody>
                  <div className="oi-header">
                    <div className="oi-header-left">
                      <div className="oi-logo">O</div>
                      <div>
                        <h5 className="oi-title">Odoo Integration (All Time)</h5>
                        <p className="oi-subtitle">
                          Falcon — Odoo data synchronization
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="oi-grid">
                    {odooModules.map((mod) => {
                      const handleOdooNavigation = () => {
                        switch (mod.moduleKey) {
                          case "CUSTOMERS":
                            openInNewTab("/admin/odoo-customers");
                            break;
                          case "SPARE_PARTS":
                            openInNewTab("/admin/odoo-spareParts");
                            break;
                          case "SERVICES":
                            openInNewTab("/admin/odoo-service");
                            break;
                          case "MANUFACTURERS":
                            openInNewTab("/admin/odoo-manufacturer");
                            break;
                          case "QUOTATIONS":
                            openInNewTab("/admin/odoo-quotation");
                            break;
                          default:
                            openInNewTab("/admin/dashboard");
                        }
                      };

                      return (
                        <div className="oi-item-card" key={mod.moduleKey}>
                          <div className="oi-item-header">
                            <span className="oi-item-name">{mod.label}</span>
                          </div>

                          <div className="oi-stats">
                            <div className="oi-stat-group">
                              <span className="oi-stat-label">SYNCED</span>
                              <span className="oi-stat-value green">
                                {mod.syncCount.toLocaleString()}
                              </span>
                            </div>
                            <div className="oi-stat-group text-right">
                              <span className="oi-stat-label">NOT SYNCED</span>
                              <span className="oi-stat-value red">
                                {mod.notSyncCount.toLocaleString()}
                              </span>
                            </div>
                          </div>

                          <div className="oi-progress-bar">
                            <div
                              className={`oi-progress-fill ${mod.isHealthy ? "green" : "amber"
                                }`}
                              style={{ width: `${mod.syncPct}%` }}
                            ></div>
                          </div>

                          <div className="oi-item-footer">
                            <span />
                            {mod.notSyncCount > 0 && (
                              <span
                                className="oi-link"
                                onClick={handleOdooNavigation}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter")
                                    handleOdooNavigation();
                                }}
                              >
                                Details →
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}

                    {odooModules.length === 0 && (
                      <div className="oi-item-card">
                        <span className="oi-item-name">
                          No sync data available
                        </span>
                      </div>
                    )}
                  </div>
                </CardBody>
              </Card>
            </Col>

            {/* ODOO SALES + QUOTATION COUNT CARD */}
            <Col lg="4" className="order-1">
              <Card className="action-required-card">
                <CardBody>
                  <div className="oi-header">
                    <div className="oi-header-left">
                      <div>
                        <h5 className="oi-title">
                          Odoo Sales and Quotation Count
                        </h5>
                      </div>
                    </div>
                  </div>
                  {/* Two half-width cards in one row */}
                  <Row
                    className="action-required-row"
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      marginLeft: "-6px",
                      marginRight: "-6px",
                    }}
                  >
                    <Col
                      xs="12"
                      sm="12"
                      md="6"
                      lg="6"
                      style={{
                        paddingLeft: "6px",
                        paddingRight: "6px",
                        display: "flex",
                        marginBottom: "12px"
                      }}
                    >
                      <div
                        className="oi-item-card"
                        style={{
                          width: "100%",
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          minWidth: 0,
                          marginBottom: "1rem"
                        }}
                      >
                        <div className="oi-item-header">
                          <span className="oi-item-name">Odoo Sales</span>
                        </div>

                        <div
                          className="oi-stats"
                          style={{
                            flex: "1 1 auto",
                            display: "flex",
                            alignItems: "center",
                            minWidth: 0,
                          }}
                        >
                          <div className="oi-stat-group">
                            <span className="oi-stat-label">TOTAL SALES</span>
                            <span
                              className="oi-stat-value green"
                              style={{
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                minWidth: 0,
                              }}
                            >
                              {odooSalesTotal?.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </span>
                          </div>
                        </div>

                        <div
                          className="oi-progress-bar"
                          style={{ marginTop: "4px", marginBottom: "10px" }}
                        >
                          <div className="oi-progress-fill green" />
                        </div>

                        <div
                          className="oi-item-footer"
                          style={{ marginTop: "auto" }}
                        >
                          <span />
                        </div>
                      </div>
                    </Col>

                    <Col
                      xs="12"
                      sm="12"
                      md="6"
                      lg="6"
                      style={{
                        paddingLeft: "6px",
                        paddingRight: "6px",
                        display: "flex",
                        marginBottom: "12px",
                      }}
                    >
                      <div
                        className="oi-item-card"
                        style={{
                          width: "100%",
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          minWidth: 0,
                        }}
                      >
                        <div className="oi-item-header">
                          <span className="oi-item-name">Odoo Quotation Count</span>
                        </div>

                        <div
                          className="oi-stats"
                          style={{
                            flex: "1 1 auto",
                            display: "flex",
                            alignItems: "center",
                            minWidth: 0,
                          }}
                        >
                          <div className="oi-stat-group">
                            <span className="oi-stat-label">TOTAL QUOTATIONS</span>
                            <span
                              className="oi-stat-value green"
                              style={{
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                minWidth: 0,
                              }}
                            >
                              {odooQuotationTotal?.toLocaleString()}
                            </span>
                          </div>
                        </div>

                        <div
                          className="oi-progress-bar"
                          style={{ marginTop: "4px", marginBottom: "10px" }}
                        >
                          <div className="oi-progress-fill green" />
                        </div>

                        <div
                          className="oi-item-footer"
                          style={{ marginTop: "auto" }}
                        >
                          <span />
                        </div>
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </Container>
    </>
  );
};

export default Dashboard;
